import { useEffect, useState } from 'react'
import { useAuthContext } from '../../../../context/auth.context'
import { getAccess } from './api'

// The organization's API access (GET /api/ddd/api-access) for the places
// that say what it means beside something else: an LMS connection's API keys
// panel and a group's external ids. The sidebar's "API keys" entry and the
// /org/api-keys pages read it too: they exist only while it is on (owner
// 2026-11-02, like the LMS feature).
//
// Read once per session and organization, and shared by every caller. A failed
// read is null, and is forgotten, so a later mount asks again. The
// API keys list and a key's page read the access afresh when they open and
// after a create or a revoke, and hand the answer to every caller
// (noteApiAccess). `enabled` is the caller's own gate (API_KEY read):
// nobody else asks.

const cache = { key: null, promise: null, value: undefined }
const listeners = new Set()
const notify = () => listeners.forEach(listener => listener())

// Tests start each case from a fresh session.
export const forgetApiAccess = () => {
  cache.key = null
  cache.promise = null
  cache.value = undefined
}

const read = key => {
  if (cache.key !== key || !cache.promise) {
    if (cache.key !== key) cache.value = undefined
    cache.key = key
    const promise = Promise.resolve()
      .then(getAccess)
      .then(access => {
        if (cache.promise === promise) {
          cache.value = access || null
          notify()
        }
        return access || null
      })
      .catch(() => {
        if (cache.promise === promise) {
          cache.promise = null
          cache.value = null
          notify()
        }
        return null
      })
    cache.promise = promise
  }
  return cache.promise
}

// An answer a page read itself (the API keys list or a key's page reads
// the access afresh when it opens, so staff switching it mid-session shows
// at once): every caller takes it, without another read. Before any caller
// has asked there is nothing to replace; it reads its own.
export const noteApiAccess = access => {
  if (!cache.key) return
  cache.value = access || null
  cache.promise = Promise.resolve(cache.value)
  notify()
}

const keyOf = state => {
  if (!state) return null
  const claims = state.jwtClaims || {}
  const session = claims.sessionID || state.loginID
  const organization = state.organizationID || claims.organizationID
  return session && organization ? `${session}:${organization}` : null
}

export const useApiAccess = enabled => {
  const { state } = useAuthContext()
  const key = enabled ? keyOf(state) : null
  const [, rerender] = useState(0)

  useEffect(() => {
    if (!key) return undefined
    const listener = () => rerender(n => n + 1)
    listeners.add(listener)
    read(key)
    return () => {
      listeners.delete(listener)
    }
  }, [key])

  if (!key || cache.key !== key) return null
  return cache.value || null
}
